/* eslint no-use-before-define: 0 */

/**
 * array of:
 * {autoCompleted, estateId, itemId, lat, lng, templateId}
 */
import { fireNewSearchDone } from "@sharedFrontend/globalEvents";

import AutocompleteService from "./autocomplete.service";
import { areaValueUpdated$ } from "../components/area-filters-panel.component";

import GeocodeService from "./geocode.service";
import RestService from "./rest.service";
import { SearchPanelComponent, searchButtonClicked$, enterKeyPressed$ } from "../components/search-panel.component";
// eslint-disable-next-line import/no-cycle
import { Components } from "../loader";
import RoomFilter from "../components/Apartments/room-filter.component";
import SizeFilter from "../components/Apartments/size-filter.component";
import RentFilter from "../components/Apartments/rent-filter.component";

export default class SearchService {
    // options: {noFilters}
    constructor(options) {
        if (options) {
            this.options = options;
        }
        this.RoomFilter = new RoomFilter();
        this.SizeFilter = new SizeFilter();
        this.RentFilter = new RentFilter();
        this.initialize();
        document.addEventListener("cookies-accept:required", () => this.handleSearchRequest());
        var tileListContainer = document.getElementsByClassName("tile-list-container");
        if (tileListContainer && tileListContainer.length > 0 && tileListContainer[0].getAttribute("data-showemptylist") == "True") {
            this.handleSearchRequest();
        }
    }

    /**
     * @private
     */
    initialize() {
        this.subscribeOnareaValueUpdated$();
        this.subscribeOnAutocompletePlaceChanged$();
        this.subscribeOnSearchButtonClicked$();
        this.subscribeOnEnterKeyPressed$();
        this.subscribeOnRoomFilterChanged$();
        this.subscribeOnSizeFilterChanged$();
        this.subscribeOnRentFilterChanged$();
    }

    /**
     * @private
     */
    subscribeOnSizeFilterChanged$() {
        if (!this.SizeFilter.initialized) {
            return;
        }

        this.SizeFilter.events.selectionChanged$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    subscribeOnRentFilterChanged$() {
        if (!this.RentFilter.initialized) {
            return;
        }

        this.RentFilter.events.selectionChanged$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    subscribeOnRoomFilterChanged$() {
        if (!this.RoomFilter.initialized) {
            return;
        }

        this.RoomFilter.events.selectionChanged$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    subscribeOnEnterKeyPressed$() {
        enterKeyPressed$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    subscribeOnSearchButtonClicked$() {
        searchButtonClicked$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    subscribeOnAutocompletePlaceChanged$() {
        const scope = this;

        /**
         * @param {Object} args - comes from AutocompleteService.autocompletePlaceChanged$
         * @returns {Object}
         */
        function formRequest(args) {
            const request = {};
            request.lat = args.lat;
            request.lng = args.lng;
            request.query = args.formatted_address;
            request.address_components = args.address_components;

            if (!(scope.options && scope.options.noFilters)) {
                scope.addTypeValueToRequest(request);
                scope.addAreaValueToRequest(request);
            }

            if (scope.RoomFilter.initialized) {
                request.roomFilter = scope.RoomFilter.getCheckboxIndexes();
            }
            if (scope.SizeFilter.initialized) {
                request.sizeFilter = scope.SizeFilter.getRange();
            }
            if (scope.RentFilter.initialized) {
                request.maximumRent = scope.RentFilter.getRange();
            }

            return request;
        }

        AutocompleteService.autocompletePlaceChanged$.subscribe((args) => {
            const request = formRequest(args);
            this.sendRequestWithHandlers(request);
        });
    }

    /**
     * @private
     */
    subscribeOnareaValueUpdated$() {
        areaValueUpdated$.subscribe(() => {
            this.handleSearchRequest();
        });
    }

    /**
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    addTypeValueToRequest(request) {
        const { selectedTypeFilter } = Components.searchPanel.typeFiltersPanel;
        if (selectedTypeFilter) {
            request.premiseTemplateIds = selectedTypeFilter.premiseIds;
        }
    }

    /**
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    addAreaValueToRequest(request) {
        const { areaValue } = Components.searchPanel.areaFiltersPanel;

        if (areaValue && areaValue.max) {
            request.maxArea = areaValue.max;
            request.minArea = areaValue.min;
        }
    }

    /**
     * @private
     */
    handleSearchRequest() {
        const request = {};

        if (!(this.options && this.options.noFilters)) {
            this.addTypeValueToRequest(request);
            this.addAreaValueToRequest(request);
        }

        if (this.RoomFilter.initialized) {
            request.roomFilter = this.RoomFilter.getCheckboxIndexes();
        }
        if (this.SizeFilter.initialized) {
            request.sizeFilter = this.SizeFilter.getRange();
        }
        if (this.RentFilter.initialized) {
            request.maximumRent = this.RentFilter.getRange();
        }

        const query = AutocompleteService.firstOptionFromHiddenAutocomplete || SearchPanelComponent.inputText;
        request.query = query;

        if (!query) {
            this.sendRequestWithHandlers(request);

            return;
        }

        GeocodeService.requestGeocode(query)
            .then((geocodeResult) => {
                request.lat = geocodeResult.lat;
                request.lng = geocodeResult.lng;
                request.address_components = geocodeResult.address_components;
                this.sendRequestWithHandlers(request);
            })
            .catch(() => this.sendRequestWithHandlers(request));
    }

    /**
     * @private
     * @param {Array<Object>} result - array of found premises
     */
    // eslint-disable-next-line class-methods-use-this
    handleSuccessfulSearchResult(result) {
        fireNewSearchDone(result);
    }

    /**
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    handleFailedSearchResult() {
        fireNewSearchDone([]);
    }

    sendRequestWithHandlers(request) {
        if (this.options && this.options.apartments) {
            request.apartments = true;
        }

        RestService.sendSearchRequest(request)
            .then(this.handleSuccessfulSearchResult)
            .catch(this.handleFailedSearchResult);
    }
}
